import { forwardRef, useEffect, useImperativeHandle, useRef, type DragEvent } from 'react';
import { Canvas, Line, type FabricObject } from 'fabric';
import { getProductProfile } from '@fotoboek/domain';
import type { PhotoFrameElement } from '@fotoboek/domain';
import { useProjectStore, type SelectionTarget } from '../../../state/projectStore';
import { buildScene, PX_PER_MM, type Scene } from './scene';
import {
  buildPageBackground,
  buildGuides,
  buildGutterLine,
  buildShape,
  buildDecoration,
  buildText,
  buildPhotoImageLayer,
  buildFrameHandle,
  buildCropFrameOutline,
} from './fabricBuilders';
import { placementFromImageRect } from './cropMath';
import {
  mergeCandidates,
  objectBoundsToCandidates,
  pageSnapCandidates,
  snapAxis,
  type SnapCandidates,
} from './snapping';
import './editor-canvas.css';

/** Screen-space snap tolerance; converted to canvas-content px by dividing by zoom. */
const SNAP_THRESHOLD_SCREEN_PX = 4;

function targetKey(target: SelectionTarget): string {
  return target.scope === 'page' ? `page:${target.pageId}` : `cover:${target.panel}`;
}

export type EditorCanvasHandle = {
  fitToContainer: () => void;
};

type ObjectInfo = {
  elementId: string;
  target: SelectionTarget;
  kind: 'frame' | 'crop-image' | 'other';
};

export const EditorCanvas = forwardRef<EditorCanvasHandle>(function EditorCanvas(_props, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<Canvas | null>(null);
  const objectInfoRef = useRef<Map<FabricObject, ObjectInfo>>(new Map());
  const suppressCommitRef = useRef(false);
  const lastSceneRef = useRef<Scene | null>(null);
  const snapGuideLinesRef = useRef<FabricObject[]>([]);

  const project = useProjectStore((s) => s.project);
  const assetsRuntime = useProjectStore((s) => s.assetsRuntime);
  const view = useProjectStore((s) => s.view);
  const activeSpreadIndex = view.mode === 'spread' ? view.spreadIndex : -1;
  const zoom = useProjectStore((s) => s.zoom);
  const cropElementId = useProjectStore((s) => s.cropElementId);
  const selection = useProjectStore((s) => s.selection);
  const setSelection = useProjectStore((s) => s.setSelection);
  const clearSelection = useProjectStore((s) => s.clearSelection);
  const updateElement = useProjectStore((s) => s.updateElement);
  const enterCropMode = useProjectStore((s) => s.enterCropMode);
  const setZoom = useProjectStore((s) => s.setZoom);
  const setElementAssetId = useProjectStore((s) => s.setElementAssetId);

  // Scene signature avoids rebuilding the whole canvas on unrelated store
  // changes (e.g. title edits) while staying simple and correct.
  const sceneSignature = project
    ? JSON.stringify({
        pages:
          view.mode === 'spread'
            ? project.pages.filter((p) => Math.floor(p.order / 2) === view.spreadIndex)
            : [],
        cover: view.mode === 'cover' ? project.cover : null,
        settings: project.settings,
        productProfileId: project.productProfileId,
        pageCount: project.pages.length,
      })
    : null;

  useEffect(() => {
    if (!canvasElRef.current) return;
    const canvas = new Canvas(canvasElRef.current, {
      backgroundColor: '#d8d2c6',
      preserveObjectStacking: true,
    });
    fabricRef.current = canvas;

    canvas.on('selection:created', handleSelectionEvent);
    canvas.on('selection:updated', handleSelectionEvent);
    canvas.on('selection:cleared', () => clearSelection());
    canvas.on('object:modified', (e) => {
      if (suppressCommitRef.current || !e.target) return;
      clearSnapGuides();
      commitObjectTransform(e.target);
    });
    canvas.on('object:moving', (e) => {
      if (!suppressCommitRef.current && e.target) handleSnapMove(e.target);
    });
    canvas.on('object:scaling', (e) => {
      if (!suppressCommitRef.current && e.target)
        handleSnapScale(e.target, e.transform?.corner ?? '');
    });
    canvas.on('mouse:up', () => clearSnapGuides());
    canvas.on('mouse:dblclick', (e) => {
      if (!e.target) return;
      const info = objectInfoRef.current.get(e.target);
      if (info?.kind === 'frame') {
        const el = findElementById(info.elementId);
        if (el && el.type === 'photoFrame' && el.assetId) {
          enterCropMode(info.elementId);
        }
      }
    });

    function handleSelectionEvent(e: { selected?: FabricObject[] }) {
      const selected = e.selected ?? [];
      const infos = selected
        .map((obj) => objectInfoRef.current.get(obj))
        .filter(Boolean) as ObjectInfo[];
      const first = infos[0];
      if (!first) return;
      setSelection({ target: first.target, elementIds: infos.map((i) => i.elementId) });
    }

    return () => {
      canvas.dispose();
      fabricRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function findElementById(elementId: string) {
    if (!project) return null;
    for (const page of project.pages) {
      const found = page.elements.find((e) => e.id === elementId);
      if (found) return found;
    }
    for (const panel of Object.values(project.cover.panels)) {
      const found = panel.elements.find((e) => e.id === elementId);
      if (found) return found;
    }
    return null;
  }

  function commitObjectTransform(obj: FabricObject) {
    const info = objectInfoRef.current.get(obj);
    if (!info) return;
    const el = findElementById(info.elementId);
    if (!el) return;

    if (info.kind === 'crop-image' && el.type === 'photoFrame') {
      const asset = assetsRuntime[el.assetId ?? ''];
      if (!asset) return;
      const clip = obj.clipPath;
      const frameOriginPx = clip
        ? { x: (clip as FabricObject).left ?? 0, y: (clip as FabricObject).top ?? 0 }
        : { x: 0, y: 0 };
      const imageRectPx = {
        x: (obj.left ?? 0) - frameOriginPx.x,
        y: (obj.top ?? 0) - frameOriginPx.y,
        width: (obj.width ?? 0) * (obj.scaleX ?? 1),
        height: (obj.height ?? 0) * (obj.scaleY ?? 1),
      };
      const frameMm = { width: el.width, height: el.height };
      const imageRectMm = {
        x: imageRectPx.x / PX_PER_MM,
        y: imageRectPx.y / PX_PER_MM,
        width: imageRectPx.width / PX_PER_MM,
        height: imageRectPx.height / PX_PER_MM,
      };
      const placement = placementFromImageRect(
        frameMm,
        { width: asset.widthPx, height: asset.heightPx },
        imageRectMm,
        el.placement.rotation,
        el.placement.flipX,
        el.placement.flipY,
      );
      updateElement(info.target, info.elementId, { placement } as Partial<PhotoFrameElement>);
      return;
    }

    const widthMm = ((obj.width ?? 0) * (obj.scaleX ?? 1)) / PX_PER_MM;
    const heightMm = ((obj.height ?? 0) * (obj.scaleY ?? 1)) / PX_PER_MM;
    const originPx = objectOriginFor(info.target);
    const xMm = ((obj.left ?? 0) - originPx.x) / PX_PER_MM;
    const yMm = ((obj.top ?? 0) - originPx.y) / PX_PER_MM;
    updateElement(info.target, info.elementId, {
      x: xMm,
      y: yMm,
      width: Math.max(2, widthMm),
      height: Math.max(2, heightMm),
      rotation: obj.angle ?? 0,
    });
  }

  const lastOriginsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  function objectOriginFor(target: SelectionTarget): { x: number; y: number } {
    const key = target.scope === 'page' ? `page:${target.pageId}` : `cover:${target.panel}`;
    return lastOriginsRef.current.get(key) ?? { x: 0, y: 0 };
  }

  function clearSnapGuides() {
    const canvas = fabricRef.current;
    if (!canvas) return;
    if (snapGuideLinesRef.current.length === 0) return;
    for (const line of snapGuideLinesRef.current) canvas.remove(line);
    snapGuideLinesRef.current = [];
    canvas.requestRenderAll();
  }

  function drawSnapGuides(vGuide: number | null, hGuide: number | null) {
    const canvas = fabricRef.current;
    const scene = lastSceneRef.current;
    if (!canvas || !scene) return;
    clearSnapGuides();
    if (vGuide === null && hGuide === null) return;

    const totalWidthPx = scene.totalSizeMm.width * PX_PER_MM;
    const totalHeightPx = scene.totalSizeMm.height * PX_PER_MM;
    const lines: FabricObject[] = [];
    const makeGuideProps = () => ({
      stroke: '#2f6f6a',
      strokeWidth: 1,
      strokeDashArray: [4, 3],
      selectable: false,
      evented: false,
      hoverCursor: 'default',
      excludeFromExport: true,
    });
    if (vGuide !== null) lines.push(new Line([vGuide, 0, vGuide, totalHeightPx], makeGuideProps()));
    if (hGuide !== null) lines.push(new Line([0, hGuide, totalWidthPx, hGuide], makeGuideProps()));

    for (const line of lines) canvas.add(line);
    snapGuideLinesRef.current = lines;
    canvas.requestRenderAll();
  }

  /** Snap targets for the item an object belongs to: page geometry + every other object on that same page/panel. */
  function collectSnapCandidatesFor(
    target: SelectionTarget,
    activeObj: FabricObject,
  ): SnapCandidates | null {
    const scene = lastSceneRef.current;
    if (!scene) return null;
    const key = targetKey(target);
    const item = scene.items.find((it) => targetKey(it.target) === key);
    if (!item) return null;

    const originPx = { x: item.originMm.x * PX_PER_MM, y: item.originMm.y * PX_PER_MM };
    let candidates = pageSnapCandidates(originPx, item.sizeMm, item.safeMarginMm, PX_PER_MM);
    if (scene.gutterLinesMm.length > 0) {
      candidates = mergeCandidates(candidates, {
        vertical: scene.gutterLinesMm.map((x) => x * PX_PER_MM),
        horizontal: [],
      });
    }

    const otherGroups: SnapCandidates[] = [];
    for (const [obj, info] of objectInfoRef.current.entries()) {
      if (obj === activeObj || info.kind === 'crop-image' || targetKey(info.target) !== key)
        continue;
      const rect = obj.getBoundingRect();
      otherGroups.push(objectBoundsToCandidates(rect));
    }

    return mergeCandidates(candidates, ...otherGroups);
  }

  function handleSnapMove(obj: FabricObject) {
    if (!useProjectStore.getState().project?.settings.snapEnabled) {
      clearSnapGuides();
      return;
    }
    const info = objectInfoRef.current.get(obj);
    if (!info || info.kind === 'crop-image') return;

    const candidates = collectSnapCandidatesFor(info.target, obj);
    if (!candidates) return;

    const threshold = SNAP_THRESHOLD_SCREEN_PX / useProjectStore.getState().zoom;
    const width = obj.getScaledWidth();
    const height = obj.getScaledHeight();
    const left = obj.left ?? 0;
    const top = obj.top ?? 0;

    const vMatch = snapAxis([left, left + width / 2, left + width], candidates.vertical, threshold);
    const hMatch = snapAxis(
      [top, top + height / 2, top + height],
      candidates.horizontal,
      threshold,
    );

    if (vMatch) obj.set('left', left + vMatch.offset);
    if (hMatch) obj.set('top', top + hMatch.offset);

    if (vMatch || hMatch) {
      drawSnapGuides(vMatch?.guide ?? null, hMatch?.guide ?? null);
    } else {
      clearSnapGuides();
    }
  }

  function handleSnapScale(obj: FabricObject, corner: string) {
    if (!useProjectStore.getState().project?.settings.snapEnabled || !corner || corner === 'mtr') {
      clearSnapGuides();
      return;
    }
    const info = objectInfoRef.current.get(obj);
    if (!info || info.kind === 'crop-image') return;

    const candidates = collectSnapCandidatesFor(info.target, obj);
    if (!candidates) return;

    const threshold = SNAP_THRESHOLD_SCREEN_PX / useProjectStore.getState().zoom;
    const width = obj.getScaledWidth();
    const height = obj.getScaledHeight();
    const left = obj.left ?? 0;
    const top = obj.top ?? 0;
    const baseWidth = obj.width || 1;
    const baseHeight = obj.height || 1;

    const patch: Record<string, number> = {};
    let vGuide: number | null = null;
    let hGuide: number | null = null;

    if (corner.includes('r')) {
      const match = snapAxis([left + width], candidates.vertical, threshold);
      if (match) {
        const newWidth = width + match.offset;
        if (newWidth > 4) {
          patch.scaleX = newWidth / baseWidth;
          vGuide = match.guide;
        }
      }
    } else if (corner.includes('l')) {
      const match = snapAxis([left], candidates.vertical, threshold);
      if (match) {
        const newLeft = left + match.offset;
        const newWidth = left + width - newLeft;
        if (newWidth > 4) {
          patch.left = newLeft;
          patch.scaleX = newWidth / baseWidth;
          vGuide = match.guide;
        }
      }
    }

    if (corner.includes('b')) {
      const match = snapAxis([top + height], candidates.horizontal, threshold);
      if (match) {
        const newHeight = height + match.offset;
        if (newHeight > 4) {
          patch.scaleY = newHeight / baseHeight;
          hGuide = match.guide;
        }
      }
    } else if (corner.includes('t')) {
      const match = snapAxis([top], candidates.horizontal, threshold);
      if (match) {
        const newTop = top + match.offset;
        const newHeight = top + height - newTop;
        if (newHeight > 4) {
          patch.top = newTop;
          patch.scaleY = newHeight / baseHeight;
          hGuide = match.guide;
        }
      }
    }

    if (Object.keys(patch).length > 0) obj.set(patch);
    if (vGuide !== null || hGuide !== null) {
      drawSnapGuides(vGuide, hGuide);
    } else {
      clearSnapGuides();
    }
  }

  // Rebuild the visible scene whenever relevant project data, view, zoom, or
  // crop mode changes.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !project) return;
    let cancelled = false;

    async function rebuild() {
      const profile = getProductProfile(project!.productProfileId);
      const scene = buildScene(project!, view, profile);
      lastSceneRef.current = scene;
      const originsByKey = new Map<string, { x: number; y: number }>();

      canvas!.clear();
      objectInfoRef.current = new Map();

      const totalWidthPx = scene.totalSizeMm.width * PX_PER_MM;
      const totalHeightPx = scene.totalSizeMm.height * PX_PER_MM;
      canvas!.setDimensions({ width: totalWidthPx * zoom, height: totalHeightPx * zoom });
      canvas!.setZoom(zoom);

      for (const item of scene.items) {
        const originPx = { x: item.originMm.x * PX_PER_MM, y: item.originMm.y * PX_PER_MM };
        const key =
          item.target.scope === 'page'
            ? `page:${item.target.pageId}`
            : `cover:${item.target.panel}`;
        originsByKey.set(key, originPx);

        canvas!.add(buildPageBackground(item, originPx));
        for (const guide of buildGuides(item, originPx, {
          bleed: project!.settings.showBleedGuides,
          trim: project!.settings.showTrimGuides,
          safe: project!.settings.showSafeZoneGuides,
        })) {
          canvas!.add(guide);
        }

        const sorted = [...item.elements]
          .filter((e) => !e.hidden)
          .sort((a, b) => a.zIndex - b.zIndex);
        for (const el of sorted) {
          if (cancelled) return;
          if (el.type === 'photoFrame') {
            const isCropTarget = cropElementId === el.id;
            const asset = el.assetId ? assetsRuntime[el.assetId] : undefined;
            if (asset) {
              const imageLayer = await buildPhotoImageLayer(el, originPx, asset, isCropTarget);
              canvas!.add(imageLayer);
              if (isCropTarget) {
                objectInfoRef.current.set(imageLayer, {
                  elementId: el.id,
                  target: item.target,
                  kind: 'crop-image',
                });
                canvas!.add(buildCropFrameOutline(el, originPx));
              }
            }
            if (!isCropTarget) {
              const handle = buildFrameHandle(el, originPx, !!asset);
              canvas!.add(handle);
              objectInfoRef.current.set(handle, {
                elementId: el.id,
                target: item.target,
                kind: 'frame',
              });
            }
          } else if (el.type === 'text') {
            const obj = buildText(el, originPx);
            canvas!.add(obj);
            objectInfoRef.current.set(obj, {
              elementId: el.id,
              target: item.target,
              kind: 'other',
            });
          } else if (el.type === 'shape') {
            const obj = buildShape(el, originPx);
            canvas!.add(obj);
            objectInfoRef.current.set(obj, {
              elementId: el.id,
              target: item.target,
              kind: 'other',
            });
          } else if (el.type === 'decoration') {
            const obj = buildDecoration(el, originPx);
            canvas!.add(obj);
            objectInfoRef.current.set(obj, {
              elementId: el.id,
              target: item.target,
              kind: 'other',
            });
          }
        }
      }

      if (project!.settings.showGutterGuides) {
        for (const xMm of scene.gutterLinesMm) {
          canvas!.add(buildGutterLine(xMm, scene.totalSizeMm.height));
        }
      }

      lastOriginsRef.current = originsByKey;

      // Restore selection after rebuild.
      if (selection.target && selection.elementIds.length > 0) {
        const objs = [...objectInfoRef.current.entries()]
          .filter(([, info]) => selection.elementIds.includes(info.elementId))
          .map(([obj]) => obj);
        const single = objs[0];
        if (objs.length === 1 && single) {
          suppressCommitRef.current = true;
          canvas!.setActiveObject(single);
          suppressCommitRef.current = false;
        }
      }

      canvas!.requestRenderAll();
    }

    void rebuild();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sceneSignature,
    view.mode,
    activeSpreadIndex,
    zoom,
    cropElementId,
    assetsRuntime,
    project?.id,
  ]);

  useImperativeHandle(ref, () => ({
    fitToContainer() {
      const canvas = fabricRef.current;
      const container = containerRef.current;
      if (!canvas || !container || !project) return;
      const profile = getProductProfile(project.productProfileId);
      const scene = buildScene(project, view, profile);
      const pad = 64;
      const availableWidth = container.clientWidth - pad;
      const availableHeight = container.clientHeight - pad;
      const baseWidth = scene.totalSizeMm.width * PX_PER_MM;
      const baseHeight = scene.totalSizeMm.height * PX_PER_MM;
      const nextZoom = Math.max(
        0.2,
        Math.min(availableWidth / baseWidth, availableHeight / baseHeight, 2),
      );
      setZoom(nextZoom);
    },
  }));

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const assetId = e.dataTransfer.getData('text/x-fotoboek-asset-id');
    if (!assetId || !project || !canvasElRef.current) return;
    const rect = canvasElRef.current.getBoundingClientRect();
    const localPxX = (e.clientX - rect.left) / zoom;
    const localPxY = (e.clientY - rect.top) / zoom;
    const profile = getProductProfile(project.productProfileId);
    const scene = buildScene(project, view, profile);
    for (const item of scene.items) {
      const originPx = { x: item.originMm.x * PX_PER_MM, y: item.originMm.y * PX_PER_MM };
      const sorted = [...item.elements]
        .filter((el) => el.type === 'photoFrame')
        .sort((a, b) => b.zIndex - a.zIndex);
      for (const el of sorted) {
        const left = originPx.x + el.x * PX_PER_MM;
        const top = originPx.y + el.y * PX_PER_MM;
        const right = left + el.width * PX_PER_MM;
        const bottom = top + el.height * PX_PER_MM;
        if (localPxX >= left && localPxX <= right && localPxY >= top && localPxY <= bottom) {
          setElementAssetId(item.target, el.id, assetId);
          return;
        }
      }
    }
  }

  return (
    <div
      className="editor-canvas-container"
      ref={containerRef}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <canvas ref={canvasElRef} />
    </div>
  );
});
